import { cn } from "@/lib/utils";
import {
  BugIcon,
  ShieldAlertIcon,
  ZapIcon,
  PaletteIcon,
  WrenchIcon,
  RefreshCwIcon,
  LayoutIcon,
  CircleIcon,
} from "lucide-react";
import type { ReviewCategory } from "@/types/assessment";

interface CommentBadgeProps {
  category: ReviewCategory;
  size?: "sm" | "md";
  showLabel?: boolean;
}

const CATEGORY_CONFIG: Record<
  ReviewCategory,
  {
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    colorClass: string;
    bgClass: string;
  }
> = {
  bug: {
    label: "Bug",
    icon: BugIcon,
    colorClass: "text-red-600 dark:text-red-400",
    bgClass: "bg-red-100 dark:bg-red-950/50",
  },
  security: {
    label: "Security",
    icon: ShieldAlertIcon,
    colorClass: "text-orange-600 dark:text-orange-400",
    bgClass: "bg-orange-100 dark:bg-orange-950/50",
  },
  performance: {
    label: "Performance",
    icon: ZapIcon,
    colorClass: "text-yellow-600 dark:text-yellow-400",
    bgClass: "bg-yellow-100 dark:bg-yellow-950/50",
  },
  style: {
    label: "Style",
    icon: PaletteIcon,
    colorClass: "text-blue-600 dark:text-blue-400",
    bgClass: "bg-blue-100 dark:bg-blue-950/50",
  },
  maintainability: {
    label: "Maintainability",
    icon: WrenchIcon,
    colorClass: "text-purple-600 dark:text-purple-400",
    bgClass: "bg-purple-100 dark:bg-purple-950/50",
  },
  concurrency: {
    label: "Concurrency",
    icon: RefreshCwIcon,
    colorClass: "text-cyan-600 dark:text-cyan-400",
    bgClass: "bg-cyan-100 dark:bg-cyan-950/50",
  },
  design: {
    label: "Design",
    icon: LayoutIcon,
    colorClass: "text-indigo-600 dark:text-indigo-400",
    bgClass: "bg-indigo-100 dark:bg-indigo-950/50",
  },
};

// Fallback for unknown categories (defensive programming)
const FALLBACK_CONFIG = {
  label: "Issue",
  icon: CircleIcon,
  colorClass: "text-gray-600 dark:text-gray-400",
  bgClass: "bg-gray-100 dark:bg-gray-950/50",
};

export function CommentBadge({
  category,
  size = "md",
  showLabel = true,
}: CommentBadgeProps) {
  const config = CATEGORY_CONFIG[category] ?? FALLBACK_CONFIG;
  const Icon = config.icon;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full font-medium",
        config.colorClass,
        config.bgClass,
        size === "sm" ? "px-1.5 py-0.5 text-xs" : "px-2 py-1 text-sm"
      )}
    >
      <Icon className={size === "sm" ? "size-3" : "size-4"} />
      {showLabel && <span>{config.label}</span>}
    </span>
  );
}

// Category selector for the comment form
interface CategorySelectorProps {
  value: ReviewCategory | undefined;
  onChange: (category: ReviewCategory) => void;
  disabled?: boolean;
}

export function CategorySelector({
  value,
  onChange,
  disabled = false,
}: CategorySelectorProps) {
  const categories: ReviewCategory[] = [
    "bug",
    "security",
    "concurrency",
    "performance",
    "style",
    "maintainability",
    "design",
  ];

  return (
    <div className="flex flex-wrap gap-1.5">
      {categories.map((category) => {
        const config = CATEGORY_CONFIG[category];
        const Icon = config.icon;
        const isSelected = value === category;

        return (
          <button
            key={category}
            type="button"
            onClick={() => !disabled && onChange(category)}
            disabled={disabled}
            className={cn(
              "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-sm transition-all",
              "border focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              isSelected
                ? cn(config.bgClass, config.colorClass, "border-current font-medium")
                : "border-border text-muted-foreground hover:text-foreground hover:bg-accent",
              disabled && "opacity-50 cursor-not-allowed"
            )}
          >
            <Icon className="size-4" />
            {config.label}
          </button>
        );
      })}
    </div>
  );
}
